import { callDeepLink, fbkBack } from '@croquiscom-pvt/web2app';
import NextRouter from 'next/router';

import { getConfigValue } from '@common/config';
import { callPostyAppFunction, isFbkApp, isPostyNativeMain, isZigzag } from '@common/device-manager';
import { CustomLogKind, sendCustomLog, Team } from '@common/log-manager';
import { Dialog } from '@common/overlay-manager';
import { getSiteIdFromHost, SiteId } from '@common/site-manager';
import { isZigzinShop } from '@common/utils';

import { getAdjustedBrowsingType } from '../browsingType';
import {
  BrowsingType,
  InquiryBrowsingType,
  OrderBrowsingType,
  PdpBrowsingType,
  UxBrowsingType,
} from '../graphql/fragment_types.2';
import { ZigzagEvent } from '../ZigzagEvent';
import { ZpayEvent } from '../ZpayEvent';
import { TransitionOptions, ZPayFnOptions } from './types';
import {
  buildUrl,
  fetchCheckoutToUrl,
  getQuerySeparator,
  handleRouteForZPay,
  isAbsoluteUrl,
  isExternalUrl,
  urlToString,
} from './utils';

import { UrlObject } from 'url';

/**
 * 요일에 따라 직진배송관 헤더 타이틀을 반환합니다.
 * 금요일, 토요일, 일요일: "주말에도 직진배송"
 * 평일: "매일매일 직진배송"
 */
const getZigzinHeaderTitle = (): string => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0: 일요일, 1: 월요일, ..., 6: 토요일
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 5 || dayOfWeek === 6; // 금, 토, 일
  return isWeekend ? '주말에도 직진배송' : '매일매일 직진배송';
};

const gotoShopHome = ({
  shop_main_domain,
  shop_id,
  origin_shop_id,
  brand_shop_id,
  origin = '',
}: {
  shop_main_domain: string;
  shop_id: string;
  origin_shop_id?: string;
  brand_shop_id?: string;
  origin?: string;
}) => {
  const site_id = getSiteIdFromHost();
  switch (site_id) {
    case SiteId.ZIGZAG: {
      // 지그재그 앱일 경우
      if (isZigzag()) {
        // 직진배송관
        if (isZigzinShop(shop_id)) {
          const header_title = getZigzinHeaderTitle();
          callDeepLink('/full_screen_simple_browser')({
            url: encodeURIComponent(
              `${getConfigValue('contentUrl')}/zigzin?zz_scene=zigzin_main&header_title=${header_title}`,
            ),
          });
          return;
        }

        // 스토어홈
        if (!origin) {
          callDeepLink(`/shops/${shop_main_domain}`)();
        } else {
          callDeepLink(`/shops/${shop_main_domain}`)({ zz_origin: origin });
        }
      } else {
        // 직진배송관
        if (isZigzinShop(shop_id)) {
          NextRouter.push(`${getConfigValue('contentUrl')}/zigzin`);
          return;
        }

        // 스토어홈
        NextRouter.push(`${getConfigValue('appUrl', site_id)}/${shop_main_domain}`);
      }
      break;
    }
    case SiteId.POSTY: {
      // 포스티는 직진배송일 경우, 브랜드홈 or origin_shop_id로 이동
      if (isZigzinShop(shop_id)) {
        if (brand_shop_id || origin_shop_id) {
          NextRouter.push(`${getConfigValue('appUrl', site_id)}/stores/${brand_shop_id || origin_shop_id}`);
        }
        return;
      }

      // 브랜드홈 or 스토어홈
      if (brand_shop_id || shop_id) {
        NextRouter.push(`${getConfigValue('appUrl', site_id)}/stores/${brand_shop_id || shop_id}`);
      }
      break;
    }
    case SiteId.FBK: {
      // 직진배송관
      if (isZigzinShop(shop_id)) {
        NextRouter.push(`${getConfigValue('appUrl', site_id)}/special-categories/zigzin`);
        return;
      }
      // 스토어홈
      NextRouter.push(`${getConfigValue('appUrl', site_id)}/store/${shop_id}`);
      break;
    }
    default:
  }
};

const gotoPDP = ({
  product_id,
  browsing_type,
  pdp_landing_url,
  shop_id,
  product_no,
  funnel_type,
  raffle_id,
  use_router_open,
  from,
  log_from,
}: {
  product_id: string;
  browsing_type: BrowsingType | PdpBrowsingType | OrderBrowsingType | UxBrowsingType | InquiryBrowsingType;
  pdp_landing_url: string | null;
  shop_id: string;
  product_no?: string | null;
  funnel_type?: string;
  raffle_id?: string | null;
  use_router_open?: boolean;
  from?: string;
  log_from?: string; // 로그 추적용으로 임시 추가
}) => {
  const site_id = getSiteIdFromHost();
  const funnel_type_query_string = funnel_type ? `funnel_type=${funnel_type}` : '';
  const from_query_string = from ? `from=${from}` : '';

  // 래플PDP 이동
  if (raffle_id) {
    switch (site_id) {
      case SiteId.ZIGZAG: {
        // 지그재그 앱일 경우
        if (isZigzag()) {
          callDeepLink(`${getConfigValue('appUrl', site_id)}/raffle/detail/${raffle_id}`)();
        } else {
          NextRouter.push(`${getConfigValue('appUrl', site_id)}/raffle/detail/${raffle_id}`);
        }
        break;
      }
      case SiteId.POSTY: {
        NextRouter.push(`${getConfigValue('appUrl', site_id)}/raffles/${raffle_id}`);
        break;
      }
      case SiteId.FBK: {
        break;
      }
      default:
    }
    return;
  }

  // pdp_landing_url 값이 없으면 PDP 이동 불가 alert 노출
  if (!pdp_landing_url) {
    Dialog.alert({
      type: 'warning',
      title: '스토어 임시 운영 중단',
      message:
        '스토어 운영상의 문제로 임시 운영 중단 처리되었습니다.\n주문 또는 문의 처리는 스토어의 상황에 따라 지연될 수 있습니다.',
    });
    return;
  }

  // PDP 이동
  switch (site_id) {
    case SiteId.ZIGZAG: {
      if (isZigzag()) {
        if (!pdp_landing_url || !product_id || !browsing_type) {
          sendCustomLog(
            `pdp_landing_url: ${pdp_landing_url}, product_id: ${product_id}, browsing_type: ${browsing_type}, log_from: ${log_from}`,
            Team.TEAM_COC,
            {
              kind: CustomLogKind.GOTO_PDP_FAILED,
            },
          );
        }
        // 지그재그 앱일 경우
        const query_separator = getQuerySeparator(pdp_landing_url || '');
        const pdp_landing_url_with_funnel: string = funnel_type
          ? buildUrl(
              pdp_landing_url || '',
              query_separator,
              funnel_type_query_string,
              from ? from_query_string : undefined,
            )
          : buildUrl(pdp_landing_url || '', query_separator, from ? from_query_string : undefined);

        switch (browsing_type) {
          case BrowsingType.Z_BROWSER:
            return callDeepLink('/product_detail')({
              browsing_type,
              url: pdp_landing_url_with_funnel,
              shop_id: shop_id,
              ...(product_no && { shop_product_no: product_no }),
              catalog_product_id: product_id,
              ...(from && { from }),
            });
          case BrowsingType.NATIVE_BROWSER:
          case BrowsingType.DETAIL_BROWSER: {
            return callDeepLink('/product_detail')({
              browsing_type: getAdjustedBrowsingType(browsing_type, pdp_landing_url),
              url: pdp_landing_url,
              shop_id: shop_id,
              ...(product_no && { shop_product_no: product_no }),
              catalog_product_id: product_id,
              ...(from && { from }),
            });
          }
          case BrowsingType.INTERNAL_BROWSER: {
            const pdp_web_url = `${getConfigValue(
              'appUrl',
              site_id,
            )}/catalog/products/${product_id}?${funnel_type_query_string}${
              funnel_type_query_string === '' ? from_query_string : `&${from_query_string}`
            }`;

            return callDeepLink('/product_detail')({
              browsing_type: getAdjustedBrowsingType(browsing_type, pdp_web_url),
              url: pdp_web_url,
              shop_id: shop_id,
              ...(product_no && { shop_product_no: product_no }),
              catalog_product_id: product_id,
            });
          }
          default:
            return callDeepLink(`${pdp_landing_url_with_funnel}`)();
        }
      } else {
        const base_url = `${getConfigValue('appUrl', site_id)}/catalog/products/${product_id}`;
        const query_string = [
          funnel_type_query_string,
          funnel_type_query_string === '' ? from_query_string : `&${from_query_string}`,
        ]
          .filter(Boolean)
          .join('');

        const pdp_landing_url = `${base_url}?${query_string}`;

        if (use_router_open) {
          window.open(pdp_landing_url, '_blank');
        } else {
          NextRouter.push(pdp_landing_url);
        }
      }
      break;
    }
    case SiteId.POSTY: {
      NextRouter.push(`${getConfigValue('appUrl', site_id)}/products/${product_id}?${funnel_type_query_string}`);
      break;
    }
    case SiteId.FBK: {
      NextRouter.push(
        `${getConfigValue('appUrl', site_id)}/catalog/products/${product_id}?${funnel_type_query_string}`,
      );
      break;
    }
    default:
  }
};

const back = (
  option: { disable_app_function: boolean; fallback?: () => void } = {
    disable_app_function: false,
  },
) => {
  const site_id = getSiteIdFromHost();
  const is_zigzag_app = isZigzag();

  if (option.disable_app_function) {
    if (option.fallback) {
      option.fallback();
    } else {
      NextRouter.back();
    }
    return;
  }
  switch (site_id) {
    case SiteId.ZIGZAG:
      if (is_zigzag_app) {
        ZigzagEvent.back();
      } else {
        NextRouter.back();
      }
      break;
    case SiteId.POSTY:
      if (isPostyNativeMain()) {
        callPostyAppFunction('back');
      } else {
        NextRouter.back();
      }
      break;
    case SiteId.FBK:
      if (isFbkApp()) {
        fbkBack();
      } else {
        NextRouter.back();
      }
      break;
  }
};

const push = (url: string | UrlObject, zpayFnOptions: ZPayFnOptions = {}, options?: TransitionOptions) => {
  url = fetchCheckoutToUrl(url);

  handleRouteForZPay(url, {
    disable_zpay_function: false,
    ...zpayFnOptions,
  })(() => {
    NextRouter.push(url, url, options);
  });
};

const replace = (url: string | UrlObject, zpayFnOptions: ZPayFnOptions = {}, options?: TransitionOptions) => {
  url = fetchCheckoutToUrl(url);
  handleRouteForZPay(url, {
    disable_zpay_function: false,
    ...zpayFnOptions,
  })(() => {
    NextRouter.replace(url, url, options);
  });
};

const open = (
  url: string | UrlObject,
  zpayFnOptions: ZPayFnOptions = {},
  /**
   * POSTY, FBK 앱에서 외부 URL 열 때 네비게이션 브라우저 타입으로 강제 오픈할지 여부
   */
  options?: { forceNavigationBrowserType?: boolean },
) => {
  const site_id = getSiteIdFromHost();
  url = fetchCheckoutToUrl(url);

  const openDefault = () => {
    handleRouteForZPay(url, {
      disable_zpay_function: false,
      ...zpayFnOptions,
    })(() => {
      window.open(url as string, '_blank');
    });
  };

  switch (site_id) {
    case SiteId.ZIGZAG:
      openDefault();
      break;
    case SiteId.POSTY:
    case SiteId.FBK:
      if (isFbkApp() || isPostyNativeMain()) {
        const target_url = urlToString(url);
        const final_url = isAbsoluteUrl(target_url) ? target_url : `${getConfigValue('appUrl', site_id)}${target_url}`;
        const browser_type_param =
          options?.forceNavigationBrowserType || isExternalUrl(final_url) ? '&browser_type=navigation' : '';

        window.location.href = `${getConfigValue('appScheme', site_id)}://open/browser?url=${encodeURIComponent(final_url)}${browser_type_param}`;
      } else {
        openDefault();
      }
      break;
    default:
      openDefault();
  }
};

const openFullScreenSimpleBrowser = (pathname: string, query?: object | undefined) => {
  const is_zigzag_app = isZigzag();
  const href = window.location.origin + pathname;

  if (is_zigzag_app) {
    callDeepLink('/full_screen_simple_browser')({
      url: encodeURIComponent(href),
      ...(query && query),
    });
  } else {
    window.open(href, '_blank');
  }
};

const backFullScreenSimpleBrowser = () => {
  const is_zigzag_app = isZigzag();

  if (is_zigzag_app) {
    ZigzagEvent.back();
  } else {
    window.close();
  }
};

const closeFullScreenSimpleBrowser = () => {
  const is_zigzag_app = isZigzag();

  if (is_zigzag_app) {
    ZigzagEvent.back();
  } else {
    window.close();
  }
};

const goHome = (search_params?: URLSearchParams) => {
  const site_id = getSiteIdFromHost();
  const is_zigzag_app = isZigzag();

  switch (site_id) {
    case SiteId.ZIGZAG:
      if (is_zigzag_app) {
        window.location.href = 'zigzag://open/home?page_type=home';
      } else {
        // Memo. NextRouter 쓰면 checkout-web의 root로 라우팅되어 window.location.href 사용
        window.location.href = `${getConfigValue('appUrl', SiteId.ZIGZAG)}`;
      }
      break;
    case SiteId.POSTY: {
      const is_posty_native_main = isPostyNativeMain();
      const search_params_string = search_params
        ? `${is_posty_native_main ? '&' : '?'}${search_params.toString()}`
        : '';

      if (is_posty_native_main) {
        window.location.href = `${getConfigValue(
          'posty_app_scheme',
        )}://open/main?main_tab_id=home&sub_tab_id=home${search_params_string}`;
      } else {
        // Memo. NextRouter 쓰면 checkout-web의 root로 라우팅되어 window.location.href 사용
        window.location.href = `${getConfigValue('appUrl', SiteId.POSTY)}${search_params_string}`;
      }
      break;
    }
    case SiteId.FBK:
      if (isFbkApp()) {
        window.location.href = `${getConfigValue('appScheme', SiteId.FBK)}://open/main?main_tab_id=1`;
      } else {
        // Memo. NextRouter 쓰면 checkout-web의 root로 라우팅되어 window.location.href 사용
        window.location.href = `${getConfigValue('appUrl', SiteId.FBK)}`;
      }
      break;
  }
};

const close = () => {
  const site_id = getSiteIdFromHost();
  const is_zigzag_app = isZigzag();

  switch (site_id) {
    case SiteId.ZIGZAG:
      if (is_zigzag_app) {
        ZpayEvent.close();
      } else {
        window.close();
      }
      break;
    case SiteId.POSTY:
      if (isPostyNativeMain()) {
        callPostyAppFunction('close');
      } else {
        window.close();
      }
      break;
    case SiteId.FBK:
      if (isFbkApp()) {
        fbkBack();
      } else {
        window.close();
      }
      break;
  }
};

export const CheckoutRouter = {
  push,
  replace,
  open,
  back,
  close,
  goHome,
  gotoShopHome,
  gotoPDP,
  openFullScreenSimpleBrowser,
  backFullScreenSimpleBrowser,
  closeFullScreenSimpleBrowser,
};

// MEMO. 기존 사용성 위해 hook 유지, hook 기능이 필요할 경우 확장해서 사용
export const useCheckoutRouter = () => {
  return CheckoutRouter;
};
